import React from 'react';

/**
 * Amazon Associates + FTC-compliant disclosure.
 *
 * Satisfies:
 *   - Amazon Associates Program Operating Agreement (the exact endorsement text)
 *   - FTC 16 CFR Part 255 (clear & conspicuous, before or at the affiliate link)
 *
 * Variants
 * --------
 *  <AmazonDisclosure />                     full footer card (default) — use at
 *                                           bottom of any page with affiliate links
 *
 *  <AmazonDisclosure variant="inline" />    compact line — use near/above a group
 *                                           of affiliate links (drawer, card, etc.)
 *
 *  <AmazonDisclosure variant="chip" />      tiny pill-sized note — use when space
 *                                           is tight (e.g. inside a chat message)
 *
 * Every variant includes a link to /disclosure for the full policy so we meet
 * the "linked where the reasonable consumer would look" bar.
 */
export const AmazonDisclosure = ({ variant = 'full' }) => {
  if (variant === 'inline') {
    return (
      <p
        data-testid="amazon-disclosure-inline"
        className="text-[11px] text-muted-foreground leading-relaxed"
      >
        <span aria-hidden="true">📦 </span>
        As an Amazon Associate, TurfPulse earns from qualifying purchases. Product
        links below are affiliate links — we may receive a commission at no extra
        cost to you.{' '}
        <a
          href="/disclosure"
          className="underline hover:text-foreground"
          target="_blank"
          rel="noopener noreferrer"
        >
          Full disclosure
        </a>
        .
      </p>
    );
  }

  if (variant === 'chip') {
    return (
      <span
        data-testid="amazon-disclosure-chip"
        className="inline-flex items-center gap-1 text-[10px] text-muted-foreground"
      >
        <span aria-hidden="true">📦</span>
        <span>
          Affiliate link.{' '}
          <a
            href="/disclosure"
            className="underline hover:text-foreground"
            target="_blank"
            rel="noopener noreferrer"
          >
            Why
          </a>
        </span>
      </span>
    );
  }

  // Full (default) — bottom-of-page legal footer card
  return (
    <div
      data-testid="amazon-disclosure"
      className="text-xs text-muted-foreground border-t border-border pt-4 mt-8"
    >
      <p>
        <strong>Affiliate Disclosure:</strong> As an Amazon Associate, TurfPulse
        earns from qualifying purchases. Product links marked "Buy on Amazon" or
        "Shop on Amazon" are affiliate links — we may receive a commission at no
        additional cost to you. Commissions help keep TurfPulse free.{' '}
        <a href="/disclosure" className="underline hover:text-foreground">
          Read our full affiliate disclosure →
        </a>
      </p>
    </div>
  );
};

export default AmazonDisclosure;
